import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect, useContext , useRef } from 'react';
import { Drawer, Button, Steps, Form, Input, Radio, notification, Popover, Select, Popconfirm, Switch, Card, Space, Typography, Alert, Flex, Tag } from 'antd';
import { InfoCircleOutlined, MailOutlined, ApiOutlined, ToolOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';
import TradingPartnersAPI from '../../../restAPI/TradingPartnersAPI';
import CompanyAPI from '../../../restAPI/CompanyAPI';
import { RefreshContext } from "../../../RefreshContext";
import { decodeToken } from "react-jwt";
import {
  getWithinCategoryEmailDuplicateError,
  mergeNotificationEmailFieldChange,
  encodeNotificationEmailsStringForSelect,
  decodeNotificationSelectValuesToEmailsString,
  decodeNotificationSelectTagValue,
} from "../../../utils/companyNotificationValidation";

const popoverContentTIN = (
  <div>
    If TIN No is not available, please
use: <br/>
EI00000000010: General Public <br/>
EI00000000020: Foreign Buyer<br/>
EI00000000030: Foreign Supplier<br/>
EI00000000040: Government Entity
  </div>
);

const popoverContentReg = (
  <div>
   For individuals,
select and input the NRIC, <br/>Passport
number or Army number.
  </div>
);

const popoverContentMSIC = (
  <div>
 Refer to link below to
obtain the correct code:<br/>
<a href="https://sdk.myinvois.hasil.gov.my/codes
/msic-codes/" target="_blank">
https://sdk.myinvois.hasil.gov.my/codes
/msic-codes</a><br/>
You can use "00000" if Not Applicable.
  </div>
);

const popoverContentSST = (
  <div>
   If SST No is not
   available, please use “NA”.
  </div>
);

const popoverContentTTX = (
  <div>
  Tourism Tax No. - If TTX No is not
  available, please use “NA”.
  </div>
);

const Context = React.createContext({
  name: 'Default',
});
const { Step } = Steps;
const TOTAL_STEPS = 4;

/** Labels for validateForm keys — shown in the update error banner when submit validation fails */
const EDIT_SUBMIT_FIELD_LABELS = {
  companyName: "General Info — Company name",
  country: "General Info — Country",
  tin: "General Info — TIN",
  sst: "General Info — SST",
  ttx: "General Info — TTX",
  registrationType: "General Info — Registration type",
  registrationValue: "General Info — Registration value",
  msicCode: "General Info — MSIC code",
  businessActivityDescription: "General Info — Business activity",
  email: "Contact — Email",
  contactNumber: "Contact — Contact number",
  addressLine1: "Contact — Address line 1",
  addressLine2: "Contact — Address line 2",
  addressLine3: "Contact — Address line 3",
  postalZone: "Contact — Postal code",
  city: "Contact — City",
  state: "Contact — State",
  projectId: "Configuration — Project",
  templateKey: "Configuration — Template",
  queue: "Configuration — Queue",
};

const NotificationOption = ({ label, enabled, emails, onToggle, onEmailsChange, error, required, emailNotificationEnabled }) => {
  const [selectValues, setSelectValues] = useState(() =>
    encodeNotificationEmailsStringForSelect(emails ?? "")
  );

  useEffect(() => {
    setSelectValues(encodeNotificationEmailsStringForSelect(emails ?? ""));
  }, [emails]);

  const handleTagsChange = (vals) => {
    const decodedStr = decodeNotificationSelectValuesToEmailsString(vals);
    setSelectValues(encodeNotificationEmailsStringForSelect(decodedStr));
    onEmailsChange(decodedStr);
  };

  const handleClearAllEmails = () => {
    setSelectValues([]);
    onEmailsChange("");
  };

  const hasMultipleNotificationEmails = selectValues.length > 1;

  return (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enabled ? 8 : 0 }}>
      <Typography.Text>{label}{required && enabled && emailNotificationEnabled && <Typography.Text type="danger"> *</Typography.Text>}</Typography.Text>
      <Switch checked={enabled} onChange={onToggle} size="small" disabled={!emailNotificationEnabled} />
    </div>
    {enabled && emailNotificationEnabled && (
      <div>
        {hasMultipleNotificationEmails && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
            <Typography.Link type="secondary" onClick={handleClearAllEmails}>
              Clear all addresses
            </Typography.Link>
          </div>
        )}
        <Select
          mode="tags"
          open={false}
          onOpenChange={() => {}}
          value={selectValues}
          onChange={handleTagsChange}
          tokenSeparators={[",", "\n"]}
          status={error ? "error" : ""}
          style={{ width: "100%", marginTop: 4 }}
          options={[]}
          tagRender={(props) => {
            const { value, closable, onClose } = props;
            return (
              <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                {decodeNotificationSelectTagValue(value)}
              </Tag>
            );
          }}
        />
        {error && <Typography.Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>{error}</Typography.Text>}
      </div>
    )}
  </div>
  );
};
const onSearch = (value) => {
  console.log('search:', value);
};
const StepForm = forwardRef(({ onRefresh, onDrawerClose }, ref) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [countryCodes, setCountryCodes] = useState([]);
  const [classificationCodes, setClassificationCodes] = useState([]);
  const [iSGetCountryCodes, setISGetCountryCodes] = useState(true);
  const [stateCodes, setStateCodes] = useState([]);
  const [msicCodes, setMsicCodes] = useState([]);
  const [iSGetMsicCodes, setISGetMsicCodes] = useState(true);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [iSGetParentCompanies, setISGetParentCompanies] = useState(true);
  const { refreshLayout } = useContext(RefreshContext);
  const [isModified, setIsModified] = useState(false);
  const [payload, setPayload] = useState({
    "_id": "",
    "companyName": "",
    "tin": "",
    "registrationType": "",
    "registrationValue": "",
    "sst": "",
    "ttx": "",
    "msicCode": "",
    "msicDescription": "",
    "countryCode": "",
    "countryName": "",
    "classificationCode":"",
    "status": 1,
    "email": "",
    "contactNumber": "",
    "addressLine1": "",
    "addressLine2": "",
    "addressLine3": "",
    "postalZone": "",
    "city": "",
    "stateName": "",
    "stateCode": "",
    "groupId": "",
    "projectId": "",
    "templateKey":"",
      "queue":"",
      "branch": false,
      "parentCompanyId": "",
      "parentCompanyName": "",
      "branchName": "",
      "originalCompanyName": "",
      "lhdnSubmission": false,
      "isTPCompanyCodeRequired": false,
      "documentLoadDays": 30,
      "pdfExtractionService": "tps",
      "excelExtractionService": "tps",
      "showTpsIntegration": false,
      "autoProcess": false,
      "emailNotification": false,
      autoEmailSuccessfulValidation: false,
      autoEmailSuccessfulValidationEmails: "",
      autoEmailFailedValidation: false,
      autoEmailFailedValidationEmails: "",
      autoEmailPreSubmissionFailure: false,
      autoEmailPreSubmissionFailureEmails: "",
      autoEmailDuplicateInvoices: false,
      autoEmailDuplicateInvoicesEmails: "",
      autoEmailTradingPartnerErrorLog: false,
      autoEmailTradingPartnerErrorLogEmails: "",
      autoEmailIncompleteTradingPartner: false,
      autoEmailIncompleteTradingPartnerEmails: "",
      autoEmailScheduledMaintenance: false,
      autoEmailScheduledMaintenanceEmails: "",
  });
  const [errors, setErrors] = useState({});
  const [updateValidationBanner, setUpdateValidationBanner] = useState(null);
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
  const [roleDetails , setRoleDetails]= useState({
    "id": "",
    "name": ""
  });
  const notificationShown = useRef(false);
  const [isMalStates, setIsMalStates] = useState(false);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openEditDrawer: (initialStep = 0) => {
      setPayload({_id: ref.id});
      getCompanyById(ref.id);
      setCurrentStep(initialStep);
      setUpdateValidationBanner(null);
      setVisible(true); // Open drawer
    },
    closeEditDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  //get Role from token
    useEffect(() => {
      //decode token
      if (token != null) {
        const decodedTokenValue = decodeToken(token);
        setRoleDetails({
          "id": decodedTokenValue.role.id ,
          "name": decodedTokenValue.role.name
        })
        console.log("User role:", decodedTokenValue.role.name);
      }
    }, [token]);

  const getCompanyById = async (id) => {   
      await CompanyAPI.getCompanyById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
          if (res?.data) {
            console.log(JSON.stringify("getCompanyById",res.data));
            let status = 1;
            if(res.data.status){
              status = 1;
            }
            else{
              status = 0;
            }
            setPayload({
              "_id": res.data._id,
              "companyName": res.data.name,
              "tin": res.data.tin,
              "registrationType": res.data.registrationType,
              "registrationValue": res.data.registrationValue,
              "sst": res.data.sst,
              "ttx": res.data.ttx,
              "msicCode": res.data.msic.msicCode,
              "msicDescription": res.data.msic.description,
              "countryCode": res.data.country.countryCode,
              "countryName": res.data.country.countryName,
              "classificationCode": res.data.classificationCode,
              "status": status,
              "email": res.data.email,
              "contactNumber": res.data.contactNumber,
              "addressLine1": res.data.address.addressLine1,
              "addressLine2": res.data.address.addressLine2,
              "addressLine3": res.data.address.addressLine3,
              "postalZone": res.data.address.postalZone,
              "city": res.data.address.city,
              "stateName": res.data.address.state.name,
              "stateCode": res.data.address.state.code,
              "groupId": res.data.groupId,
              "projectId": res.data.projectId,
              "templateKey":res.data.templateKey,
              "queue":res.data.queue,
              "branch": res.data.isBranch || false,
              "parentCompanyId": res.data.parentCompanyId?._id || res.data.parentCompanyId || "",
              "parentCompanyName": res.data.parentCompanyId?.name || "",
              "branchName": res.data.isBranch ? res.data.name : "",
              "originalCompanyName": res.data.name,
              "lhdnSubmission": res.data.lhdnSubmission || false,
              "isTPCompanyCodeRequired": res.data.isTPCompanyCodeRequired || false,
              "documentLoadDays": res.data.documentLoadDays || 30,
              "pdfExtractionService": res.data.pdfExtractionService || "tps",
              "excelExtractionService": res.data.excelExtractionService || "tps",
              "showTpsIntegration": (res.data.pdfExtractionService === 'tps' || res.data.excelExtractionService === 'tps') ? true : false,
              "autoProcess": res.data.autoProcess || false,
              "emailNotification": res.data.emailNotification || false,
              autoEmailSuccessfulValidation: res.data.autoEmailSuccessfulValidation || false,
              autoEmailSuccessfulValidationEmails: res.data.autoEmailSuccessfulValidationEmails || "",
              autoEmailFailedValidation: res.data.autoEmailFailedValidation || false,
              autoEmailFailedValidationEmails: res.data.autoEmailFailedValidationEmails || "",
              autoEmailPreSubmissionFailure: res.data.autoEmailPreSubmissionFailure || false,
              autoEmailPreSubmissionFailureEmails: res.data.autoEmailPreSubmissionFailureEmails || "",
              autoEmailDuplicateInvoices: res.data.autoEmailDuplicateInvoices || false,
              autoEmailDuplicateInvoicesEmails: res.data.autoEmailDuplicateInvoicesEmails || "",
              autoEmailTradingPartnerErrorLog: res.data.autoEmailTradingPartnerErrorLog || false,
              autoEmailTradingPartnerErrorLogEmails: res.data.autoEmailTradingPartnerErrorLogEmails || "",
              autoEmailIncompleteTradingPartner: res.data.autoEmailIncompleteTradingPartner || false,
              autoEmailIncompleteTradingPartnerEmails: res.data.autoEmailIncompleteTradingPartnerEmails || "",
              autoEmailScheduledMaintenance: res.data.autoEmailScheduledMaintenance || false,
              autoEmailScheduledMaintenanceEmails: res.data.autoEmailScheduledMaintenanceEmails || "",
            });
            
            if(res.data.country.countryCode ==="MYS")
              {
                  getStateCodes();
              }
              else
              {
                setPayload((prev)=>({
                  ...prev,
                  stateName: "Not Applicable",
                  stateCode: "17"}));
              }

            // Load parent companies and set company name if this is a branch company
            if (res.data.isBranch) {
              getParentCompanies();
              // Set company name to parent company name for display
              if (res.data.parentCompanyId?.name) {
                setPayload((prev) => ({
                  ...prev,
                  companyName: res.data.parentCompanyId.name
                }));
              }
            }
          }
      }).catch(err => {
          console.log("error getting company data :", err.message);
      })
    };

  const [api,contextHolder] = notification.useNotification();
  // const openNotificationWithIcon = (type, message, description, placement) => {
  //   api[type]({
  //     message: message,
  //     description: description,
  //     placement
  // });
  // };
  
    const openNotificationWithIcon = (type, message, description, placement) => {
      if (!notificationShown.current) {
        notification[type]({
          message: message,
          description: description,
          placement,
        });
        notificationShown.current = true;
        setTimeout(() => {
          notificationShown.current = false;
        }, 5000);
      }
    };

  const contextValue = useMemo(
    () => ({
      name: 'Ant Design',
    }),
    [],
  );

  useEffect(() => {
      if(iSGetCountryCodes){
        getCountryCodes();
        getClassificationCodes();
        setISGetCountryCodes(false);
      }
  
      // if(iSGetStateCodes){
      //   getStateCodes();
      //   setISGetStateCodes(false);
      // }
  
      if(iSGetMsicCodes){
        getMsicCodes();
        setISGetMsicCodes(false);
      }

      if(iSGetParentCompanies){
        getParentCompanies();
        setISGetParentCompanies(false);
      }
    },);

      const getClassificationCodes = async () => {
        await CompanyAPI.GetcompanyClassificationCodes(
          token,
          localStorage.getItem("groupId"),
          localStorage.getItem("companyId")
        )
          .then((res) => {
            if (res?.data) {
              //console.log(JSON.stringify(res.data));
              const rows = [];
              res.data.map((rowValue) => {
                rows.push({ value: rowValue.code, label: rowValue.code });
              });
              rows.sort((a, b) => a.value.localeCompare(b.value));
              setClassificationCodes(rows)
            }
          })
          .catch((err) => {
            console.log("error getting classification codes :", err.message);
          });
      };
  
    const getCountryCodes = async () => {   
      //alert(companyId);
      await TradingPartnersAPI.getCountryCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            //console.log(JSON.stringify(res.data));
            const rows = [];
            res.data.map((rowValue) =>  {
                rows.push({value: rowValue.code, label: rowValue.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') })
            });
            rows.sort((a, b) => a.label.localeCompare(b.label));
            const malaysiaIndex = rows.findIndex(item => item.value.toUpperCase() === "MYS");
            const [malaysia] = rows.splice(malaysiaIndex, 1);    
            rows.unshift(malaysia);
            setCountryCodes(rows);
  
          }
      }).catch(err => {
          console.log("error getting contry codes data :", err.message);
      })
    };
  
    const getStateCodes = async () => {   
      await TradingPartnersAPI.getStateCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            console.log('states',JSON.stringify(res.data));
            const rows = [];
            res.data.map((rowValue) =>  {
                rows.push({value: rowValue.code, label: rowValue.state})
            });
            //rows.push({ value: "17", label: "Not Applicable" })
            setStateCodes(rows);
          }
      }).catch(err => {
          console.log("error getting state codes data :", err.message);
      })
    };
  
    const getMsicCodes = async () => {   
      await TradingPartnersAPI.getMsciCodes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
          if (res?.data) {
            //console.log(JSON.stringify(res.data));
            const rows = [];
            res.data.sort((a, b) => a.code.localeCompare(b.code))
            res.data.map((rowValue) =>  {
              rows.push({
                value: rowValue.code,
                label: `${rowValue.code} - ${rowValue.description}`, //string for search
                code: rowValue.code,
                description: rowValue.description,
              });
            });
            setMsicCodes(rows);
          }
      }).catch(err => {
          console.log("error getting msic codes data :", err.message);
      })
    };

    const getParentCompanies = async () => {
      await CompanyAPI.getParentCompaniesForLoggedUser(
        token,
        localStorage.getItem("groupId"),
        localStorage.getItem("companyId"),
      )
        .then((res) => {
          if (res?.data) {
            const rows = [];
            res.data.map((rowValue) => {
              // Exclude current company from parent companies list in edit mode
              if (rowValue._id !== payload._id) {
                rows.push({
                  value: rowValue._id,
                  label: rowValue.name
                });
              }
            });
            setParentCompanies(rows);
          }
        })
        .catch((err) => {
          console.log("error getting parent companies data :", err.message);
        });
    };

    const handleBranchToggle = (checked) => {
      setPayload((prev) => ({
        ...prev,
        branch: checked,
        ...(checked ? {} : {
          parentCompanyId: "",
          parentCompanyName: "",
          branchName: "",
          companyName: prev.originalCompanyName || ""
        }),
        ...(checked ? { originalCompanyName: prev.companyName } : {})
      }));
      if (checked) {
        getParentCompanies();
      }
      setIsModified(true);
    };

    const OnParentCompanyChange = async (value) => {
      const selectedParent = parentCompanies.find(company => company.value === value);
      if (selectedParent) {
        try {
          // Fetch parent company details
          const response = await CompanyAPI.getCompanyById(
            token,
            localStorage.getItem("groupId"),
            localStorage.getItem("companyId"),
            value
          );
          
          if (response?.data) {
            const parentData = response.data;
            setPayload((prev) => ({
              ...prev,
              parentCompanyId: value,
              parentCompanyName: selectedParent.label,
              companyName: selectedParent.label,
              // Auto-populate from parent company data
              tin: parentData.tin || "",
              sst: parentData.sst || "",
              ttx: parentData.ttx || "",
              registrationType: parentData.registrationType || "Business registration number",
              registrationValue: parentData.registrationValue || "",
              msicCode: parentData.msic?.msicCode || "",
              msicDescription: parentData.msic?.description || "",
              countryCode: parentData.country?.countryCode || "",
              countryName: parentData.country?.countryName || "",
              stateName: parentData.address?.state?.name || "",
              stateCode: parentData.address?.state?.code || "",
              email: parentData.email || "",
              contactNumber: parentData.contactNumber || "",
              addressLine1: "", // Keep editable
              addressLine2: "", // Keep editable  
              addressLine3: "", // Keep editable
              postalZone: parentData.address?.postalZone || "",
              city: parentData.address?.city || "",
              classificationCode: parentData.classificationCode || "",
              status: 1, // Set to Active by default when parent company is selected
            }));
            
            // Ensure state codes are loaded if the parent company is from Malaysia
            if (parentData.country?.countryCode === "MYS") {
              getStateCodes();
            }
          }
        } catch (error) {
          console.error("Error fetching parent company details:", error);
          // Fallback to basic selection
          setPayload((prev) => ({
            ...prev,
            parentCompanyId: value,
            parentCompanyName: selectedParent.label,
            companyName: selectedParent.label,
            status: 1, // Set to Active by default when parent company is selected
            lhdnSubmission: false, // Default to false in fallback case
          }));
        }
        setIsModified(true);
      }
    };

    const checkMandatoryValidations = () => {
      const validateData = {
        companyName: payload.companyName,
        country: payload.countryName,
        country: payload.countryCode,
        tin: payload.tin,
        sst: payload.sst,
        ttx: payload.ttx,
        registrationType: payload.registrationType,
        registrationValue: payload.registrationValue,
        msicCode: payload.msicCode,
        businessActivityDescription: payload.msicDescription,
        ...(roleDetails.name === "System Admin" && { queue: payload.queue })
      }
      const validationResult = validateForm(validateData, validationRules);
    
      if (!validationResult.isValid) {
          const validationErr = validationResult.errors;
          const newErrors = {};
          Object.keys(validationErr).forEach((value) => {
              newErrors[value] = validationErr[value];
          })
          setErrors(newErrors);
          return false;
      } else {
          return true;
      }
    }

  const next = () => {
    const validationStatus = (currentStep < 2) ? checkMandatoryValidations() : true;
    if (validationStatus) {
      setUpdateValidationBanner(null);
      setCurrentStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
    }
  };
  const prev = () => {
    setUpdateValidationBanner(null);
    setCurrentStep(s => Math.max(s - 1, 0));
  };

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
        const err = validationResult.errors
        const newErrors = {};
        Object.keys(err).forEach((value) => {
            newErrors[value] = err[value];
        })
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
        const keyData = Object.keys(data)[0]
        setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  const OnCountryChange = (value) => {
    let contry = countryCodes.filter( function (obj) {
      return obj.value === value
    });

    if(contry.length > 0){
      contry.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": rowValue.value,
          "countryName": rowValue.label,
          "classificationCode":payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "projectId": payload.projectId,
          "templateKey":payload.templateKey,
          "queue":payload.queue,
          "branch": payload.branch,
        });
      });
      setErrors({});
      setIsModified(true);
    }

    if(value ==="MYS")
      {
          getStateCodes();
          setPayload((prev)=>({
            ...prev,
            stateName: "",
            stateCode: ""}));
      }
      else
      {
        setIsMalStates(true)
        setPayload((prev)=>({
          ...prev,
          stateName: "Not Applicable",
          stateCode: "17"}));
      }  
  }

  const OnclassificationCodeChange =(value) =>{
    setPayload((prev)=>({
      ...prev,
      classificationCode: value,
    })); 
    setIsModified(true);
  }

  const OnRegistrationTypeChange = (value) => {
    setPayload({
      "_id": payload._id,
      "companyName": payload.companyName,
      "tin": payload.tin,
      "registrationType": value,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "classificationCode": payload.classificationCode,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "projectId": payload.projectId,
      "templateKey":payload.templateKey,
      "queue":payload.queue,
      "branch": payload.branch,
    });
    setErrors({});
    setIsModified(true);
  }

  const OnMsicChange = (value) => {
    let msic = msicCodes.filter( function (obj) {
      return obj.value === value
    });
  
    if(msic.length > 0){
      msic.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": rowValue.value,
          "msicDescription": rowValue.description,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "classificationCode": payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "projectId": payload.projectId,
          "templateKey":payload.templateKey,
          "queue":payload.queue,
          "branch": payload.branch,
        });
      });
      setErrors({});
      setIsModified(true);
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
        "companyName": payload.companyName,
        "tin": payload.tin,
        "registrationType": payload.registrationType,
        "registrationValue": payload.registrationValue,
        "sst": payload.sst,
        "ttx": payload.ttx,
        "msicCode": payload.msicCode,
        "msicDescription": payload.msicDescription,
        "countryCode": payload.countryCode,
        "countryName": payload.countryName,
        "classificationCode":payload.classificationCode,
        "status": e.target.value,
        "email": payload.email,
        "contactNumber": payload.contactNumber,
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "stateName": payload.stateName,
        "stateCode": payload.stateCode,
        "groupId": localStorage.getItem('groupId'),
        "projectId": payload.projectId,
        "templateKey":payload.templateKey,
        "queue":payload.queue,
        "branch": payload.branch,
    });
    setIsModified(true);
  };

  const OnStateChange = (value) => {
    let state = stateCodes.filter( function (obj) {
      return obj.value === value
    });
    
    if(state.length > 0){
      state.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "companyName": payload.companyName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "classificationCode": payload.classificationCode,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": rowValue.label,
          "stateCode": rowValue.value,
          "groupId": localStorage.getItem('groupId'),
          "projectId": payload.projectId,
          "templateKey":payload.templateKey,
          "queue":payload.queue,
          "branch": payload.branch,
        });
      });

      setErrors({}) ;
      setIsModified(true);
    }
  }

  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    if (onDrawerClose) onDrawerClose(); // Reset sidebar selection to Company List
    setPayload({
      "_id": "",
      "companyName": "",
      "tin": "",
      "registrationType": "",
      "registrationValue": "",
      "sst": "",
      "ttx": "",
      "msicCode": "",
      "msicDescription": "",
      "countryCode": "",
      "countryName": "",
      "classificationCode":"",
      "status": 1,
      "email": "",
      "contactNumber": "",
      "addressLine1": "",
      "addressLine2": "",
      "addressLine3": "",
      "postalZone": "",
      "city": "",
      "stateName": "",
      "stateCode": "",
      "groupId": "",
      "projectId":"",
      "templateKey":"",
      "queue":"",
      "branch": false,
      "parentCompanyId": "",
      "parentCompanyName": "",
      "branchName": "",
      "originalCompanyName": "",
      "lhdnSubmission": false,
      "isTPCompanyCodeRequired": false,
      "documentLoadDays": 30,
      "pdfExtractionService": "tps",
      "excelExtractionService": "tps",
      "showTpsIntegration": false,
      "autoProcess": false,
      "emailNotification": false,
    });
    setErrors({});
    setCurrentStep(0);
    setIsModified(false);
    setUpdateValidationBanner(null);
  } 

  const onClearChange = () => {
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    getCompanyByIdForClear(payload._id);
    setErrors({});
  } 

  const getCompanyByIdForClear = async (id) => {   
    setIsModified(false);
    await CompanyAPI.getCompanyById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
        if (res?.data) {
          console.log("EDit retrieve data",JSON.stringify(res.data));
          let status = 1;
          if(res.data.status){
            status = 1;
          }
          else{
            status = 0;
          }
          if(currentStep === 0){
            if(res.data.email !== payload.email){
              setIsModified(true);
            } if(res.data.contactNumber !== payload.contactNumber){
              setIsModified(true);
            }if(res.data.address.addressLine1 !== payload.addressLine1){
              setIsModified(true);
            }if(res.data.address.addressLine2 !== payload.addressLine2){
              setIsModified(true);
            }if(res.data.address.addressLine3 !== payload.addressLine3){
              setIsModified(true);
            }if(res.data.address.postalZone !== payload.postalZone){
              setIsModified(true);
            }if(res.data.address.city !== payload.city){
              setIsModified(true);
            }if(res.data.address.state.code !== payload.stateCode){
              setIsModified(true);
            }
            if(res.data.projectId !== payload.projectId){
              setIsModified(true);
            }if(res.data.templateKey !== payload.templateKey){
              setIsModified(true);
            }if(res.data.queue !== payload.queue){
              setIsModified(true);
            }
            if(res.data.classificationCode !== payload.classificationCode){
              setIsModified(true);
            }
            setPayload({
              "_id": res.data._id,
              "companyName": res.data.name,
              "tin": res.data.tin,
              "registrationType": res.data.registrationType,
              "registrationValue": res.data.registrationValue,
              "sst": res.data.sst,
              "ttx": res.data.ttx,
              "msicCode": res.data.msic.msicCode,
              "msicDescription": res.data.msic.description,
              "countryCode": res.data.country.countryCode,
              "countryName": res.data.country.countryName,
              "classificationCode":res.data.classificationCode,
              "status": status,
              "email": payload.email,
              "contactNumber": payload.contactNumber,
              "addressLine1": payload.addressLine1,
              "addressLine2": payload.addressLine2,
              "addressLine3": payload.addressLine3,
              "postalZone": payload.postalZone,
              "city": payload.city,
              "stateName": (res.data.country.countryCode !== "MYS") ? "Not Applicable" : payload.stateName,
              "stateCode": (res.data.country.countryCode !== "MYS") ? "17": payload.stateCode,
              "groupId": payload.groupId,
              "projectId": res.data.projectId,
              "templateKey":res.data.templateKey,
              "queue":res.data.queue,
            });
          }else if (currentStep === 1){
            if(res.data.name !== payload.companyName){
              setIsModified(true);
            }if(res.data.tin !== payload.tin){
              setIsModified(true);
            }if(res.data.registrationType !== payload.registrationType){
              setIsModified(true);
            }if(res.data.registrationValue !== payload.registrationValue){
              setIsModified(true);
            }
            if(res.data.sst !== payload.sst){
              setIsModified(true);
            }
            if(res.data.ttx !== payload.ttx){
              setIsModified(true);
            }
            if(res.data.msic.msicCode !== payload.msicCode){
              setIsModified(true);
            }
            if(res.data.msic.description !== payload.msicDescription){
              setIsModified(true);
            }
            if(res.data.country.countryCode !== payload.countryCode){
              setIsModified(true);
            }if(res.data.classificationCode !== payload.classificationCode){
              console.log("res.data.classificationCode 1",res.data.classificationCode)
              console.log("payload.classificationCode 1",payload.classificationCode)
              setIsModified(true);
            }
            
            setPayload({
              "_id": res.data._id,
              "companyName": payload.companyName,
              "tin": payload.tin,
              "registrationType": payload.registrationType,
              "registrationValue": payload.registrationValue,
              "sst": payload.sst,
              "ttx": payload.ttx,
              "msicCode": payload.msicCode,
              "msicDescription": payload.description,
              "countryCode": payload.countryCode,
              "countryName": payload.countryName,
              "classificationCode":payload.classificationCode,
              "status": payload.status,
              "email": res.data.email,
              "contactNumber": res.data.contactNumber,
              "addressLine1": res.data.address.addressLine1,
              "addressLine2": res.data.address.addressLine2,
              "addressLine3": res.data.address.addressLine3,
              "postalZone": res.data.address.postalZone,
              "city": res.data.address.city,
              "stateName": res.data.address.state.name,
              "stateCode": res.data.address.state.code,
              "groupId": res.data.groupId,
              "projectId": payload.projectId,
              "templateKey":payload.templateKey,
              "queue":payload.queue,
            });
          } else if (currentStep === 2 || currentStep === 3) {
            setPayload({
              ...payload,
              branch: res.data.isBranch || false,
              status: status,
              projectId: res.data.projectId || "",
              templateKey: res.data.templateKey || "",
              queue: res.data.queue || "",
              lhdnSubmission: res.data.lhdnSubmission || false,
              isTPCompanyCodeRequired: res.data.isTPCompanyCodeRequired || false,
              documentLoadDays: res.data.documentLoadDays || 30,
              autoProcess: res.data.autoProcess || false,
              emailNotification: res.data.emailNotification || false,
              autoEmailSuccessfulValidation: res.data.autoEmailSuccessfulValidation || false,
              autoEmailSuccessfulValidationEmails: res.data.autoEmailSuccessfulValidationEmails || "",
              autoEmailFailedValidation: res.data.autoEmailFailedValidation || false,
              autoEmailFailedValidationEmails: res.data.autoEmailFailedValidationEmails || "",
              autoEmailPreSubmissionFailure: res.data.autoEmailPreSubmissionFailure || false,
              autoEmailPreSubmissionFailureEmails: res.data.autoEmailPreSubmissionFailureEmails || "",
              autoEmailDuplicateInvoices: res.data.autoEmailDuplicateInvoices || false,
              autoEmailDuplicateInvoicesEmails: res.data.autoEmailDuplicateInvoicesEmails || "",
              autoEmailTradingPartnerErrorLog: res.data.autoEmailTradingPartnerErrorLog || false,
              autoEmailTradingPartnerErrorLogEmails: res.data.autoEmailTradingPartnerErrorLogEmails || "",
              autoEmailIncompleteTradingPartner: res.data.autoEmailIncompleteTradingPartner || false,
              autoEmailIncompleteTradingPartnerEmails: res.data.autoEmailIncompleteTradingPartnerEmails || "",
              autoEmailScheduledMaintenance: res.data.autoEmailScheduledMaintenance || false,
              autoEmailScheduledMaintenanceEmails: res.data.autoEmailScheduledMaintenanceEmails || "",
            });
            setIsModified(false);
          }
        }
    }).catch(err => {
        console.log("error getting company data :", err.message);
    })
  };

  const validateBeforeSubmit = () => {
    const pass = () => ({ ok: true });
    /** inlineOnly: true = only show errors.notificationSection / field errors on the step; skip duplicate top banner */
    const fail = (title, items = [], { inlineOnly = false } = {}) => ({
      ok: false,
      title,
      items,
      inlineOnly,
    });

    const validateData = {
      companyName: payload.companyName,
      country: payload.countryName,
      country: payload.countryCode,
      tin: payload.tin,
      sst: payload.sst,
      ttx: payload.ttx,
      registrationType: payload.registrationType,
      registrationValue: payload.registrationValue,
      msicCode: payload.msicCode,
      businessActivityDescription: payload.msicDescription,
      email: payload.email,
      contactNumber: payload.contactNumber,
      addressLine1: payload.addressLine1,
      addressLine2: payload.addressLine2,
      addressLine3: payload.addressLine3,
      postalZone: payload.postalZone,
      city: payload.city,
      state: payload.stateName,
      state: payload.stateCode,
      projectId: payload.projectId,
      templateKey:payload.templateKey,
      ...(roleDetails.name === "System Admin" && { queue: payload.queue }) // Only include queue validation for System Admin
    }
    const validationResult = validateForm(validateData, validationRules);
    if (!validationResult.isValid) {
        const validationErr = validationResult.errors;
        const newErrors = {};
        Object.keys(validationErr).forEach((value) => {
            newErrors[value] = validationErr[value];
        })
        setErrors((prev) => ({ ...prev, ...newErrors }));
        const items = Object.entries(validationErr).map(
          ([key, msg]) =>
            `${EDIT_SUBMIT_FIELD_LABELS[key] || key}: ${msg}`
        );
        return fail("Fix the following fields (use Previous to go to the right step):", items);
    }
    const isSystemAdmin = roleDetails.name === "System Admin";
    const emailFieldsAll = [
        [payload.autoEmailSuccessfulValidation, payload.autoEmailSuccessfulValidationEmails, 'autoEmailSuccessfulValidationEmails', 'Success Status'],
        [payload.autoEmailFailedValidation, payload.autoEmailFailedValidationEmails, 'autoEmailFailedValidationEmails', 'Failed Validation Status'],
        [payload.autoEmailPreSubmissionFailure, payload.autoEmailPreSubmissionFailureEmails, 'autoEmailPreSubmissionFailureEmails', 'Failed Status'],
        [payload.autoEmailDuplicateInvoices, payload.autoEmailDuplicateInvoicesEmails, 'autoEmailDuplicateInvoicesEmails', 'Duplicate Invoices'],
        [payload.autoEmailTradingPartnerErrorLog, payload.autoEmailTradingPartnerErrorLogEmails, 'autoEmailTradingPartnerErrorLogEmails', 'Trading Partner Error Log'],
        [payload.autoEmailIncompleteTradingPartner, payload.autoEmailIncompleteTradingPartnerEmails, 'autoEmailIncompleteTradingPartnerEmails', 'Incomplete Trading Partner'],
        [payload.autoEmailScheduledMaintenance, payload.autoEmailScheduledMaintenanceEmails, 'autoEmailScheduledMaintenanceEmails', 'Scheduled Maintenance']
    ];
    const emailFields = isSystemAdmin
        ? emailFieldsAll
        : emailFieldsAll.filter((row) => row[2] !== "autoEmailScheduledMaintenanceEmails");
    const anyNotificationCategoryEnabled =
      payload.autoEmailSuccessfulValidation ||
      payload.autoEmailFailedValidation ||
      payload.autoEmailPreSubmissionFailure ||
      payload.autoEmailDuplicateInvoices ||
      payload.autoEmailTradingPartnerErrorLog ||
      payload.autoEmailIncompleteTradingPartner ||
      payload.autoEmailScheduledMaintenance;
    if (anyNotificationCategoryEnabled && !payload.emailNotification) {
      const msg =
        "Enable Email Notification in the Configuration section before using notification categories.";
      setErrors((prev) => ({
        ...prev,
        notificationSection: msg,
      }));
      return fail("Notification configuration", [msg], { inlineOnly: true });
    }
    const notificationErrors = {};
    let hasEnabledNotification = false;
    for (const [enabled, emails, key] of emailFields) {
        if (!enabled) continue;
        hasEnabledNotification = true;
        if (emails && String(emails).trim()) {
            const withinErr = getWithinCategoryEmailDuplicateError(emails);
            if (withinErr) {
                notificationErrors[key] = withinErr;
                continue;
            }
        }
        if (payload.emailNotification) {
            if (!emails || !emails.trim()) {
                notificationErrors[key] =
                  "Email addresses are required for each notification category you enable.";
            } else {
                const emailErr = validationRules.commaSeparatedEmails(emails);
                if (emailErr) {
                    notificationErrors[key] = emailErr;
                }
            }
        } else if (emails) {
            const emailErr = validationRules.commaSeparatedEmails(emails);
            if (emailErr) {
                notificationErrors[key] = emailErr;
            }
        }
    }
    if (Object.keys(notificationErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...notificationErrors }));
        const items = Object.entries(notificationErrors).map(([errKey, msg]) => {
          const row = emailFieldsAll.find((r) => r[2] === errKey);
          const label = row ? row[3] : errKey;
          return `${label}: ${msg}`;
        });
        return fail("Notification section — correct the following:", items);
    }
    if (!isSystemAdmin && payload.autoEmailScheduledMaintenance) {
        hasEnabledNotification = true;
    }
    if (payload.emailNotification && !hasEnabledNotification) {
        const msg =
          "When Email Notification is enabled, turn on at least one notification category and add at least one email address.";
        setErrors(prev => ({ ...prev, notificationSection: msg }));
        return fail("Notification configuration", [msg], { inlineOnly: true });
    }
    setErrors(prev => ({ ...prev, notificationSection: '' }));
    return pass();
  }

  const onSubmit = async () => {
    setShowPopconfirm(false);
    setUpdateValidationBanner(null);
    const formData = {
      "name": payload.branch ? payload.branchName : payload.companyName,
      "tin": payload.tin,
      "registrationType": payload.registrationType,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msic": {
        "msicCode": payload.msicCode,
        "description": payload.msicDescription,
      },
      "country": {
        "countryName": payload.countryName,
        "countryCode": payload.countryCode,
      },
      "classificationCode":payload.classificationCode,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "address": {
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "state": {
          "name": payload.stateName,
          "code": payload.stateCode,
        }
      },
      "groupId": localStorage.getItem('groupId'),
      "projectId": payload.projectId,
      "templateKey":payload.templateKey,
      "queue":payload.queue,
      "isBranch": payload.branch,
      "parentCompanyId": payload.branch ? payload.parentCompanyId : undefined,
      "lhdnSubmission": payload.lhdnSubmission,
      "isTPCompanyCodeRequired": payload.isTPCompanyCodeRequired,
      "documentLoadDays": payload.documentLoadDays,
      "autoProcess": payload.autoProcess,
      "emailNotification": payload.emailNotification,
      autoEmailSuccessfulValidation: payload.autoEmailSuccessfulValidation,
      autoEmailSuccessfulValidationEmails: payload.autoEmailSuccessfulValidationEmails || "",
      autoEmailFailedValidation: payload.autoEmailFailedValidation,
      autoEmailFailedValidationEmails: payload.autoEmailFailedValidationEmails || "",
      autoEmailPreSubmissionFailure: payload.autoEmailPreSubmissionFailure,
      autoEmailPreSubmissionFailureEmails: payload.autoEmailPreSubmissionFailureEmails || "",
      autoEmailDuplicateInvoices: payload.autoEmailDuplicateInvoices,
      autoEmailDuplicateInvoicesEmails: payload.autoEmailDuplicateInvoicesEmails || "",
      autoEmailTradingPartnerErrorLog: payload.autoEmailTradingPartnerErrorLog,
      autoEmailTradingPartnerErrorLogEmails: payload.autoEmailTradingPartnerErrorLogEmails || "",
      autoEmailIncompleteTradingPartner: payload.autoEmailIncompleteTradingPartner,
      autoEmailIncompleteTradingPartnerEmails: payload.autoEmailIncompleteTradingPartnerEmails || "",
      autoEmailScheduledMaintenance: payload.autoEmailScheduledMaintenance,
      autoEmailScheduledMaintenanceEmails: payload.autoEmailScheduledMaintenanceEmails || "",
    };
    
    
    
    
    // Test if the field is actually being sent
    if (formData.isTPCompanyCodeRequired === undefined) {
      console.error("ERROR: isTPCompanyCodeRequired is undefined in formData!");
    } else {
      console.log("SUCCESS: isTPCompanyCodeRequired is defined in formData:", formData.isTPCompanyCodeRequired);
    }
  
    await CompanyAPI.updateCompany(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId'), payload._id, formData)
      .then((response) => {
        //console.log(response);
          onClose();
          refreshLayout();
          openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
      })
      .catch((err) => {
          if (err.response) {
              openNotificationWithIcon('error', 'Company update failed', `${err.response.data.message}`, 'bottomLeft');
          } else {
              openNotificationWithIcon('error', 'Company update failed', `${err.message}`, 'bottomLeft');
          }
      });
  }
  
  const handleCreateUpdateClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const result = validateBeforeSubmit();
    if (result.ok) {
      setUpdateValidationBanner(null);
      setShowPopconfirm(true);
    } else {
      setShowPopconfirm(false);
      if (result.inlineOnly) {
        setUpdateValidationBanner(null);
      } else {
        setUpdateValidationBanner({
          title: result.title,
          items: result.items || [],
        });
      }
    }
  };

  const handleCancel = () => {
    setCancelButtonDisabled(true);
    //openNotificationWithIcon('error', 'Abort', `${actionState === "add" ? "Create" : "Update"} cancelled`, 'bottomLeft')
    setTimeout(() => {
        setShowPopconfirm(false);
        setCancelButtonDisabled(false);
    }, 300);
  };

  const handleNotificationEmailsChange = (payloadKey, valueStr) => {
    setPayload((prev) => ({ ...prev, [payloadKey]: valueStr }));
    setIsModified(true);
    setErrors((prev) => {
      const next = mergeNotificationEmailFieldChange(prev, payloadKey);
      const withinErr = getWithinCategoryEmailDuplicateError(valueStr);
      if (withinErr) {
        next[payloadKey] = withinErr;
        return next;
      }
      if (valueStr.trim()) {
        const formatErr = validationRules.commaSeparatedEmails(valueStr);
        if (formatErr) next[payloadKey] = formatErr;
      }
      return next;
    });
  };

  // Content for each step
  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <Form layout="vertical">
        {/* <Alert message="* Fields are required" type="warning" /> */}
           <Form.Item 
             label={
               <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                 Company Name
                 {payload.branch && (
                   <Popover 
                     content="Please Select your Parent Company Here" 
                     title="Parent Company Selection" 
                     trigger="hover"
                   >
                     <InfoCircleOutlined className="info-icon" style={{ color: '#1890ff' }} />
                   </Popover>
                 )}
               </div>
             }
             validateStatus={errors.companyName || errors.parentCompanyId ? 'error' : ''} 
             help={errors.companyName || errors.parentCompanyId || ''} 
             required
           >
             {payload.branch ? (
               <Select
                 showSearch
                 size="large"
                 placeholder="Select Parent Company"
                 value={payload.parentCompanyId || undefined}
                 optionFilterProp="label"
                 onChange={OnParentCompanyChange}
                 options={parentCompanies}
                 filterOption={(input, option) =>
                   option?.label?.toLowerCase().includes(input.toLowerCase())
                 }
               />
             ) : (
               <Input 
                 size="large" 
                 name='companyName' 
                 value={payload["companyName"]} 
                 onChange={(e) => { 
                   setPayload(prev => ({...prev, companyName: e.target.value})); 
                   const validateData = {companyName: e.target.value}; 
                   validateFormData(validateData); 
                   setIsModified(true); 
                 }}  
                 placeholder="Company Name" 
               />
             )}
           </Form.Item>

           {payload.branch && (
             <Form.Item
               label="Branch Name"
               validateStatus={errors.branchName ? 'error' : ''}
               help={errors.branchName || ''}
               required
             >
               <Input
                 size="large"
                 name="branchName"
                 value={payload["branchName"]}
                 onChange={(e) => {
                   setPayload((prev) => ({
                     ...prev,
                     branchName: e.target.value,
                   }));
                   const validateData = { branchName: e.target.value };
                   validateFormData(validateData);
                   setIsModified(true);
                 }}
                 placeholder="Branch Name"
               />
             </Form.Item>
           )}

          <Form.Item label="Country" validateStatus={errors.country ? 'error' : ''} help={errors.country || ''} required>
          <Select
           showSearch
           name='country'
           placeholder="Select country"
           value={payload["countryCode"]}
           optionFilterProp="label"
           onChange={OnCountryChange}
           size='large'
           disabled={payload.branch}
           options={countryCodes}
           />
          </Form.Item>
          <Form.Item label="Classification Code"  >
          <Select
            showSearch
            name='classificationCode'
            size="large"
            placeholder="Classification Code"
            optionFilterProp="label"
            value={payload["classificationCode"]}
            onChange={OnclassificationCodeChange}
            disabled={payload.branch}
            options={classificationCodes}
          />
          </Form.Item>
          <Form.Item label={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          TIN
          <Popover content={popoverContentTIN} title="TIN" trigger="hover">
            <InfoCircleOutlined className="info-icon" />
          </Popover>
        </div>
      } validateStatus={errors.tin ? 'error' : ''} help={errors.tin || ''} required>
            <Input size="large" name="tin" value={payload["tin"]} disabled={payload.branch} onChange={(e) => { setPayload(prev => ({...prev, tin: e.target.value})); const validateData = {tin: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="TIN" />
          </Form.Item>

          <Form.Item  label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
               SST
                <Popover content={popoverContentSST} title="Sales & Service Tax No" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            } validateStatus={errors.sst ? 'error' : ''} help={errors.sst || ''} required>
            <Input size="large" name="sst" value={payload["sst"]} disabled={payload.branch} onChange={(e) => { setPayload(prev => ({...prev, sst: e.target.value})); const validateData = {sst: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="SST" />
          </Form.Item>

          <Form.Item label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
               TTX
                <Popover content={popoverContentTTX} title="Tourism Tax No" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            } validateStatus={errors.ttx ? 'error' : ''} help={errors.ttx || ''} >
            <Input size="large" name="ttx" value={payload["ttx"]} disabled={payload.branch} onChange={(e) => { setPayload(prev => ({...prev, ttx: e.target.value})); const validateData = {ttx: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="TTX" />
          </Form.Item>

          <Form.Item label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Registration Type  
                <Popover content={popoverContentReg} title="Registration Type" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            } validateStatus={errors.registrationType ? 'error' : ''} help={errors.registrationType || ''} required>
            <Select
              showSearch
              name="registrationType" 
              placeholder="Select Registration Type"
              optionFilterProp="label"
              value={payload["registrationType"]}
              onChange={OnRegistrationTypeChange}
              size='large'
              disabled={payload.branch}
              onSearch={onSearch}
              options={[
                {
                  value: 'NRIC',
                  label: 'NRIC',
                },
                {
                  value: 'Passport number',
                  label: 'Passport Number',
                },
                {
                  value: 'Business registration number',
                  label: 'Business Registration Number',
                },
                {
                  value: 'Army number',
                  label: 'Army Number',
                },
              ]}/>
          </Form.Item>

          <Form.Item
            label="Registration Value"
            validateStatus={errors.registrationValue ? "error" : ""}
            help={errors.registrationValue || ""}
            required
          >
            <Input
              size="large"
              name="registrationValue"
              value={payload["registrationValue"]}
              disabled={payload.branch}
              onChange={(e) => {
                setPayload((prev) => ({
                  ...prev,
                  registrationValue: e.target.value,
                }));
                const validateData = { registrationValue: e.target.value };
                validateFormData(validateData);
                setIsModified(true);
              }}
              placeholder="Registration Value"
            />
          </Form.Item>

          <Form.Item  label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                MSIC Code
                <Popover content={popoverContentMSIC} title="MSIC Code" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            } validateStatus={errors.msicCode ? 'error' : ''} help={errors.msicCode || ''} required>
            <Select
              showSearch
              name="msicCode"
              placeholder="Select MSIC Code"
              optionFilterProp="label"
              value={payload["msicCode"]+ " - " + payload["msicDescription"]}
              onChange={OnMsicChange}
              size='large'
              disabled={payload.branch}
              options={msicCodes}
              optionRender={(option) => (
                <div
                  style={{
                    whiteSpace: "normal",
                    lineHeight: "1.5em",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  <strong>{option.data.code}</strong> - {option.data.description}
                </div>
              )}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          {/* <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
             Business Activity Description
              <Popover content={popoverContentBu} title="Business Activity Description" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } required>
            <Input size="large" name="businessActivityDescription" placeholder="Business Activity Description" disabled value={payload["businessActivityDescription"]}/>
          </Form.Item> */}


         
        </Form>
      );
    } else if (currentStep === 1) {
      return <div> <Form layout="vertical" >
        <Form.Item label="Email Address" validateStatus={errors.email ? 'error' : ''} help={errors.email || ''} required>
          <Input size="large" name="email" value={payload["email"]} onChange={(e) => { setPayload(prev => ({...prev, email: e.target.value})); const validateData = {email: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="Email Address" />
        </Form.Item>

        <Form.Item label="Contact Number" validateStatus={errors.contactNumber ? 'error' : ''} help={errors.contactNumber || ''} required >
          <Input size="large"  name="contactNumber" value={payload["contactNumber"]} onChange={(e) => { setPayload(prev => ({...prev, contactNumber: e.target.value})); const validateData = {contactNumber: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="Contact Number" />
        </Form.Item>

        <Form.Item label="Address">
          <Form.Item label="Address Line 1" validateStatus={errors.addressLine1 ? 'error' : ''} help={errors.addressLine1 || ''} required>
            <Input size="large" name="addressLine1" value={payload["addressLine1"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine1: e.target.value})); setIsModified(true); const validateData = {addressLine1: e.target.value}; validateFormData(validateData);}} placeholder="Address Line 1" />
          </Form.Item>

          <Form.Item label="Address Line 2" validateStatus={errors.addressLine2 ? 'error' : ''} help={errors.addressLine2 || ''}>
            <Input size="large" name="addressLine2" value={payload["addressLine2"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine2: e.target.value})); setIsModified(true); const validateData = {addressLine2: e.target.value}; validateFormData(validateData);}} placeholder="Address Line 2" />
          </Form.Item>

          <Form.Item label="Address Line 3" validateStatus={errors.addressLine3 ? 'error' : ''} help={errors.addressLine3 || ''}>
            <Input size="large" name="addressLine3" value={payload["addressLine3"]} onChange={(e) => { setPayload(prev => ({...prev, addressLine3: e.target.value})); setIsModified(true); const validateData = {addressLine3: e.target.value}; validateFormData(validateData);}} placeholder="Address Line 3" />
          </Form.Item>

          <Form.Item label="Postal Zone" validateStatus={errors.postalZone ? 'error' : ''} help={errors.postalZone || ''}>
            <Input size="large" name="postalZone" value={payload["postalZone"]} onChange={(e) => { setPayload(prev => ({...prev, postalZone: e.target.value})); setIsModified(true); const validateData = {postalZone: e.target.value}; validateFormData(validateData);}} placeholder="Postal Zone" />
          </Form.Item>

          <Form.Item label="City Name" validateStatus={errors.city ? 'error' : ''} help={errors.city || ''} required >
            <Input size="large" name="city" value={payload["city"]} onChange={(e) => { setPayload(prev => ({...prev, city: e.target.value})); const validateData = {city: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="City Name" />
          </Form.Item>

          <Form.Item label="State" validateStatus={errors.state ? 'error' : ''} help={errors.state || ''} required>
            <Select
              showSearch
              name="state"
              placeholder="Select State"
              optionFilterProp="label"
              value={payload["stateName"] || "Select State"}
              onChange={OnStateChange}
              size='large'
              options={stateCodes}
              disabled = {payload.countryCode !=="MYS"}
            />
          </Form.Item>

          {/* <Form.Item label="State Code" required>
            <Input size="large" name="stateCode" placeholder="State Code" disabled value={payload["stateCode"]}/>
          </Form.Item> */}
        </Form.Item>
      </Form></div>;
    } else if (currentStep === 2) {
      return (
        <Form layout="vertical">
          <Form.Item label="Status" required>
            <Radio.Group name="status" value={payload.status} onChange={onRadioChange}>
              <Radio value={1}>Active</Radio>
              <Radio value={0}>Inactive</Radio>
            </Radio.Group>
          </Form.Item>
          {(roleDetails.name === "System Admin") && (
            <>
              <Form.Item label="Project Id">
                <Input
                  size="large"
                  name="projectId"
                  maxLength={50}
                  placeholder="Project Id"
                  value={payload.projectId}
                  onChange={(e) => {
                    setPayload((prev) => ({ ...prev, projectId: e.target.value }));
                    const validateData = { projectId: e.target.value };
                    validateFormData(validateData);
                    setIsModified(true);
                  }}
                />
              </Form.Item>
              <Form.Item label="Template Key">
                <Input
                  size="large"
                  name="templateKey"
                  maxLength={50}
                  placeholder="Template Key"
                  value={payload.templateKey}
                  onChange={(e) => {
                    setPayload((prev) => ({ ...prev, templateKey: e.target.value }));
                    const validateData = { templateKey: e.target.value };
                    validateFormData(validateData);
                    setIsModified(true);
                  }}
                />
              </Form.Item>
              <Form.Item label="Queue Number" validateStatus={errors.queue ? "error" : ""} help={errors.queue || ""} required>
                <Input
                  size="large"
                  name="queue"
                  placeholder="Queue Number"
                  value={payload.queue}
                  maxLength={3}
                  onChange={(e) => {
                    if (!isNaN(e.target.value)) {
                      setPayload((prev) => ({ ...prev, queue: e.target.value }));
                      const validateData = { queue: e.target.value };
                      validateFormData(validateData);
                      setIsModified(true);
                    }
                  }}
                />
              </Form.Item>
            </>
          )}
          {(roleDetails.name === "System Admin" || roleDetails.name === "Company Admin" || roleDetails.name === "Group Admin") && (
            <Form.Item
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  LHDN Submission
                  <Popover content="When enabled, this will block invoices from submitting to LHDN validation." title="LHDN Submission" trigger="hover">
                    <InfoCircleOutlined className="info-icon" />
                  </Popover>
                </div>
              }
            >
              <Switch
                checked={payload.lhdnSubmission}
                onChange={(checked) => {
                  setPayload((prev) => ({ ...prev, lhdnSubmission: checked }));
                  setIsModified(true);
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Use Trading Partner Company Code
                <Popover content="Activating this option makes the Trading Partner Company Code mandatory." title="Use Trading Partner Company Code" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch
              checked={payload.isTPCompanyCodeRequired}
              onChange={(checked) => {
                setPayload((prev) => ({ ...prev, isTPCompanyCodeRequired: checked }));
                setIsModified(true);
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Document Load Days
                <Popover content="Number of days of data to load by default in Document Management tables. This determines the default date range for loading documents." title="Document Load Days" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Select
              value={payload.documentLoadDays}
              onChange={(value) => {
                setPayload((prev) => ({ ...prev, documentLoadDays: value }));
                setIsModified(true);
              }}
              style={{ width: '100%' }}
              size="large"
            >
              <Select.Option value={10}>10 Days</Select.Option>
              <Select.Option value={20}>20 Days</Select.Option>
              <Select.Option value={30}>30 Days</Select.Option>
              <Select.Option value={40}>40 Days</Select.Option>
              <Select.Option value={50}>50 Days</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Auto Process
                <Popover content="If you enable this, Document Submission process will be done systematically." title="Auto Process" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch
              checked={payload.autoProcess}
              onChange={(checked) => {
                setPayload((prev) => ({ ...prev, autoProcess: checked }));
                setIsModified(true);
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Email Notification
                <Popover content="When enabled, email notifications will be sent to users. All notification section fields become mandatory when this is activated." title="Email Notification" trigger="hover">
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch
              checked={payload.emailNotification}
              onChange={(checked) => {
                setPayload((prev) => ({ ...prev, emailNotification: checked }));
                setErrors((prev) => ({ ...prev, notificationSection: '' }));
                setIsModified(true);
              }}
            />
          </Form.Item>
        </Form>
      );
    } else if (currentStep === 3) {
      return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!payload.emailNotification && (
            <Alert
              message="Email Notification is off"
              description="Turn on Email Notification in the Configuration section (previous step) before you can enable notification categories and add email addresses."
              type="warning"
              showIcon
            />
          )}
          {payload.emailNotification && (
            <Alert
              message="Email Notification is enabled"
              description="Each notification category you turn on must include at least one valid email address. You must enable at least one category when Email Notification is on."
              type="info"
              showIcon
            />
          )}
          {errors.notificationSection && (
            <Alert message={errors.notificationSection} type="error" showIcon />
          )}
          <Card className="notification-section-card" size="small" title={<><MailOutlined style={{ marginRight: 8 }} />Validation &amp; Submission</>} bordered>
            <NotificationOption label="Success Status" enabled={payload.autoEmailSuccessfulValidation} emails={payload.autoEmailSuccessfulValidationEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailSuccessfulValidation: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailSuccessfulValidationEmails", valueStr)} error={errors.autoEmailSuccessfulValidationEmails} required={payload.emailNotification} />
            <NotificationOption label="Failed Validation Status" enabled={payload.autoEmailFailedValidation} emails={payload.autoEmailFailedValidationEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailFailedValidation: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailFailedValidationEmails", valueStr)} error={errors.autoEmailFailedValidationEmails} required={payload.emailNotification} />
            <NotificationOption label="Failed Status" enabled={payload.autoEmailPreSubmissionFailure} emails={payload.autoEmailPreSubmissionFailureEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailPreSubmissionFailure: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailPreSubmissionFailureEmails", valueStr)} error={errors.autoEmailPreSubmissionFailureEmails} required={payload.emailNotification} />
            <NotificationOption label="Duplicate Invoices" enabled={payload.autoEmailDuplicateInvoices} emails={payload.autoEmailDuplicateInvoicesEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailDuplicateInvoices: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailDuplicateInvoicesEmails", valueStr)} error={errors.autoEmailDuplicateInvoicesEmails} required={payload.emailNotification} />
          </Card>
          <Card className="notification-section-card" size="small" title={<><ApiOutlined style={{ marginRight: 8 }} />Trading Partner</>} bordered>
            <NotificationOption label="Trading Partner Error Log" enabled={payload.autoEmailTradingPartnerErrorLog} emails={payload.autoEmailTradingPartnerErrorLogEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailTradingPartnerErrorLog: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailTradingPartnerErrorLogEmails", valueStr)} error={errors.autoEmailTradingPartnerErrorLogEmails} required={payload.emailNotification} />
            <NotificationOption label="Incomplete Trading Partner" enabled={payload.autoEmailIncompleteTradingPartner} emails={payload.autoEmailIncompleteTradingPartnerEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailIncompleteTradingPartner: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailIncompleteTradingPartnerEmails", valueStr)} error={errors.autoEmailIncompleteTradingPartnerEmails} required={payload.emailNotification} />
          </Card>
          {roleDetails.name === "System Admin" && (
          <Card className="notification-section-card" size="small" title={<><ToolOutlined style={{ marginRight: 8 }} />System Maintenance</>} bordered>
            <NotificationOption label="Scheduled Maintenance Events" enabled={payload.autoEmailScheduledMaintenance} emails={payload.autoEmailScheduledMaintenanceEmails} emailNotificationEnabled={payload.emailNotification} onToggle={(c) => { setPayload(prev => ({ ...prev, autoEmailScheduledMaintenance: c })); setIsModified(true); setErrors(prev => ({ ...prev, notificationSection: '' })); }} onEmailsChange={(valueStr) => handleNotificationEmailsChange("autoEmailScheduledMaintenanceEmails", valueStr)} error={errors.autoEmailScheduledMaintenanceEmails} required={payload.emailNotification} />
          </Card>
          )}
        </Space>
      );
    }
    return null;
  };

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title={
          <Flex align="center" justify="space-between" style={{ width: "100%", paddingRight: 8 }}>
            <Typography.Text strong style={{ fontSize: 16 }}>
              Update Company
            </Typography.Text>
            <Flex align="center" gap={8}>
              <Typography.Text type="secondary">Branch</Typography.Text>
              <Switch checked={payload.branch} onChange={handleBranchToggle} />
            </Flex>
          </Flex>
        }
        width={700}
        visible={visible}
        onClose={() => onClose()}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={onClearChange}>
            Reset
              </Button>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} size="large" onClick={prev}>
                Previous
              </Button>
            )}
            {currentStep < TOTAL_STEPS - 1 ? (
              <Button type="primary" size="large" onClick={next}>
                Next
              </Button>
            ) : (
              <Popconfirm
                title={`Do you want to update the company details?`}
                open={showPopconfirm}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) setShowPopconfirm(false);
                }}
                getPopupContainer={() => document.body}
                zIndex={2100}
                onConfirm={() => onSubmit()}
                onCancel={handleCancel}
                okText="Yes"
                cancelText="No"
                cancelButtonProps={{ disabled: cancelButtonDisabled }}
              >
              <Button type="primary" size="large" disabled={!isModified} onClick={handleCreateUpdateClick}>
                Update
              </Button> 
              </Popconfirm>
            )}
          </div>
        }
      >
        <Steps current={currentStep}>
          <Step title="General Info" />
          <Step title="Contact" />
          <Step title="Configuration" />
          <Step title="Notification" />
        </Steps>
        <div style={{ marginTop: 24 }}>
          {updateValidationBanner ? (
            <Alert
              type="error"
              showIcon
              closable
              message={updateValidationBanner.title}
              description={
                updateValidationBanner.items?.length ? (
                  <ul style={{ margin: "8px 0 0", paddingLeft: 20, marginBottom: 0 }}>
                    {updateValidationBanner.items.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : null
              }
              onClose={() => setUpdateValidationBanner(null)}
              style={{ marginBottom: 16 }}
            />
          ) : null}
          {renderStepContent()}
        </div>

      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
